import React from "react";
import { ComparisonSection } from "./ComparisonSection";
import { CTASection } from "./CTASection";
import { DocumentPreviewSection } from "./DocumentPreviewSection";
import { PrivacySection } from "./PrivacySection";
import { TestimonialsSection } from "./TestimonialsSection";

const MOBILE_BREAKPOINT_PX = 768;
const RESIZE_DEBOUNCE_MS = 150;

interface HomeMarketingSectionsProps {
  handleSignUp: (e?: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  showDocumentPreview?: boolean;
}

export const HomeMarketingSections: React.FC<HomeMarketingSectionsProps> = ({
  handleSignUp,
  showDocumentPreview = true,
}) => {
  const documentPreviewRef = React.useRef<HTMLElement>(null);
  const nswSectionRef = React.useRef<HTMLElement>(null);
  const finalCtaRef = React.useRef<HTMLElement>(null);

  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BREAKPOINT_PX : false
  );

  React.useEffect(() => {
    let resizeTimer: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT_PX);
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  React.useEffect(() => {
    if (isMobile) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const target = entry.target;
          const animateUnderline =
            target.classList.contains("section-header") ||
            target.classList.contains("document-preview-section");

          target.classList.add(animateUnderline ? "animate-underline" : "animate-in");
          observer.unobserve(target);
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -5% 0px",
      }
    );

    [
      documentPreviewRef.current,
      nswSectionRef.current,
      finalCtaRef.current,
    ].forEach((element) => {
      if (element) observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [isMobile, showDocumentPreview]);

  return (
    <>
      {showDocumentPreview && (
        <DocumentPreviewSection
          documentPreviewRef={documentPreviewRef}
          isMobile={isMobile}
          handleSignUp={handleSignUp}
        />
      )}

      <ComparisonSection />

      <TestimonialsSection isMobile={isMobile} />

      <PrivacySection sectionRef={nswSectionRef} />

      <CTASection finalCtaRef={finalCtaRef} handleSignUp={handleSignUp} />
    </>
  );
};
